import { useApiScope } from '@commenda/api/scope';
import { FileUploadStatus, SingleFileUploader } from '@commenda/kakashi';
import type { ValidationRules } from '@commenda/types';
import { LegacyDocumentType } from '@prisma/client';
import { useCallback, useContext, useMemo } from 'react';
import { DEFAULT_MAX_FILE_SIZE_MB } from '../../dsl-constants';
import { FormContext } from '../../hooks/FormContext';
import { buildShareScopedFileUpload } from '../../utils/share-scoped-upload';
import { isRuleActive } from '../../utils/validation';
import { LabelWithGuidanceTooltip } from './LabelWithGuidanceTooltip';

export const FileUploadComponent = ({
  fieldId,
  label,
  description,
  props = {},
  guidance,
  validation,
}: {
  fieldId: string;
  label: string;
  description?: string;
  props?: {
    acceptedFileTypes?: string[];
    maxSizeInMB?: number;
    tooltip?: string;
    documentType?: LegacyDocumentType;
  };
  guidance?: { text?: string };
  validation?: {
    rules?: ValidationRules;
    triggers?: string[];
  };
}) => {
  const {
    formData,
    updateField,
    errors,
    validateField,
    setFieldUploading,
    isFieldVisible,
  } = useContext(FormContext);
  const { shareToken } = useApiScope();
  const shareScopedUpload = useMemo(
    () => buildShareScopedFileUpload(shareToken),
    [shareToken]
  );

  const handleUploadStatusChange = useCallback(
    (status: FileUploadStatus | null) => {
      setFieldUploading(fieldId, status === FileUploadStatus.UPLOADING);
    },
    [fieldId, setFieldUploading]
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const file = formData[fieldId] as any;
  const {
    acceptedFileTypes = ['.pdf'],
    maxSizeInMB = DEFAULT_MAX_FILE_SIZE_MB,
    tooltip,
    documentType = LegacyDocumentType.Other,
  } = props;

  const isRequired = isRuleActive(validation?.rules?.required, isFieldVisible);

  // Build accept object with support for multiple MIME types per extension
  // CSV and Excel files can have multiple MIME types depending on the system/browser
  const accept: Record<string, string[]> = {};

  const addAccept = (mimeType: string, ext: string) => {
    if (!accept[mimeType]) accept[mimeType] = [];
    if (!accept[mimeType].includes(ext)) accept[mimeType].push(ext);
  };

  acceptedFileTypes.forEach((type: string) => {
    if (type === '.csv') {
      addAccept('text/csv', '.csv');
      addAccept('application/csv', '.csv');
      addAccept('text/plain', '.csv');
      addAccept('text/x-csv', '.csv');
      addAccept('application/x-csv', '.csv');
      addAccept('application/vnd.ms-excel', '.csv');
    } else if (type === '.xls') {
      addAccept('application/vnd.ms-excel', '.xls');
      addAccept('application/x-msexcel', '.xls');
    } else if (type === '.xlsx') {
      addAccept(
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        '.xlsx'
      );
      addAccept('application/vnd.ms-excel', '.xlsx');
      addAccept('application/octet-stream', '.xlsx');
    } else if (type === '.pdf') {
      addAccept('application/pdf', '.pdf');
    } else if (type === '.doc') {
      addAccept('application/msword', '.doc');
    } else if (type === '.docx') {
      addAccept(
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        '.docx'
      );
    } else if (type === '.jpg' || type === '.jpeg') {
      addAccept('image/jpeg', type);
    } else if (type === '.png') {
      addAccept('image/png', '.png');
    } else if (type === '.txt') {
      addAccept('text/plain', '.txt');
    } else if (type === '.json') {
      addAccept('application/json', '.json');
      addAccept('text/json', '.json');
    }
  });

  const handleFileChange = (f: unknown) => {
    updateField(fieldId, f);
    if (validation?.rules) {
      validateField(fieldId, f, validation.rules);
    }
  };

  return (
    <div className="mb-4">
      {(label || description) && (
        <LabelWithGuidanceTooltip
          label={label}
          description={description}
          guidance={guidance?.text}
          isRequired={isRequired}
        />
      )}
      <SingleFileUploader
        file={file}
        setFile={handleFileChange}
        accept={accept}
        maxSize={maxSizeInMB * 1024 * 1024}
        message={`Max file size: ${maxSizeInMB}MB. Accepted formats: ${acceptedFileTypes.join(', ')}`}
        companyId={undefined}
        documentType={documentType}
        error={errors[fieldId]}
        onFileUpload={shareScopedUpload}
        onUploadStatusChange={handleUploadStatusChange}
      />
      {tooltip && (
        <p className="mt-1 txt-small-regular text-text-tertiary">{tooltip}</p>
      )}
    </div>
  );
};
